import React from "react";
import {
  MdCloudUpload,
  MdCheckCircle,
  MdErrorOutline,
  MdDelete,
} from "react-icons/md";
import AttachmentTypeIcon from "components/ui/AttachmentTypeIcon";
import { resolveAttachmentFileKind } from "components/ui/attachmentFileKind";
import { splitFilename } from "lib/filenameParts";
import { r2Service, type UploadGroup } from "services/r2Service";
import { useAppLanguage } from "hooks/useAppLanguage";

export type UploadStatus = "pending" | "uploading" | "done" | "error";

export type UploadedFile = {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  key?: string;
  url?: string;
  /** Blob URL for instant image/video preview; revoked on remove or unmount. */
  localPreviewUrl?: string;
  /** Display name sent to the API (defaults to file.name when unset). */
  displayName?: string;
  error?: string;
};

export type FileUploaderProps = {
  group?: UploadGroup;
  /** If true, allow multiple files; otherwise clear previous on new drop. Default true. */
  multiple?: boolean;
  accept?: string;
  maxSizeMb?: number;
  /** Called whenever the list mutates (added / removed / status changed). */
  onChange?: (files: UploadedFile[]) => void;
  /** Disable the whole uploader. */
  disabled?: boolean;
  className?: string;
  /** Optional label override. */
  label?: React.ReactNode;
  /** Optional hint text under the label. */
  hint?: React.ReactNode;
};

function randomId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function revokePreview(entry: UploadedFile) {
  if (entry.localPreviewUrl) {
    URL.revokeObjectURL(entry.localPreviewUrl);
  }
}

function makeLocalPreview(file: File): string | undefined {
  const t = file.type || "";
  if (t.startsWith("image/") || t.startsWith("video/")) {
    return URL.createObjectURL(file);
  }
  return undefined;
}

function FileRowPreview({ entry }: { entry: UploadedFile }) {
  const [broken, setBroken] = React.useState(false);
  const type = entry.file.type || "";
  const src = entry.localPreviewUrl ?? entry.url;
  const label = entry.displayName ?? entry.file.name;
  const kind = resolveAttachmentFileKind(type, label);

  if (broken || !src) {
    return <AttachmentTypeIcon contentType={type} fileName={label} size="md" />;
  }

  if (kind === "image") {
    return (
      <div className="h-14 w-14 shrink-0">
        <img
          src={src}
          alt=""
          className="h-14 w-14 rounded-xl object-cover shadow-sm ring-1 ring-gray-200/90 dark:ring-white/10"
          onError={() => setBroken(true)}
        />
      </div>
    );
  }

  if (kind === "video") {
    return (
      <div className="h-14 w-14 shrink-0">
        <video
          src={src}
          muted
          playsInline
          preload="metadata"
          className="h-14 w-14 rounded-xl object-cover shadow-sm ring-1 ring-gray-200/90 dark:ring-white/10"
          onError={() => setBroken(true)}
        />
      </div>
    );
  }

  return <AttachmentTypeIcon contentType={type} fileName={label} size="md" />;
}

const FileNameInput = React.memo(function FileNameInput({
  id,
  base,
  lockedExt,
  disabled,
  isArabic,
  onChangeName,
}: {
  id: string;
  base: string;
  lockedExt: string;
  disabled?: boolean;
  isArabic: boolean;
  onChangeName: (id: string, nextDisplayName: string) => void;
}) {
  const [draft, setDraft] = React.useState(base);
  const focusedRef = React.useRef(false);

  React.useEffect(() => {
    // Keep the input stable while the user is typing; progress updates cause rerenders.
    if (!focusedRef.current) setDraft(base);
  }, [base]);

  return (
    <input
      type="text"
      value={draft}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
        // Normalize on blur in case parent value changed while typing.
        setDraft((v) => v);
      }}
      onChange={(e) => {
        const nextBase = e.target.value;
        setDraft(nextBase);
        onChangeName(id, nextBase + lockedExt);
      }}
      disabled={disabled}
      className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm font-medium text-gray-800 outline-none focus:border-brand-400 disabled:opacity-60 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:focus:border-brand-500"
      aria-label={isArabic ? "اسم الملف" : "File name"}
    />
  );
});

/**
 * Drag-drop multi-file uploader backed by presigned R2 uploads.
 * - Each file has its own progress row and can be removed or retried.
 * - One failing file does not block the rest.
 */
export default function FileUploader({
  group = "misc",
  multiple = true,
  accept,
  maxSizeMb,
  onChange,
  disabled,
  className = "",
  label,
  hint,
}: FileUploaderProps) {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [files, setFiles] = React.useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const filesRef = React.useRef(files);
  filesRef.current = files;

  React.useEffect(() => {
    return () => {
      for (const f of filesRef.current) {
        revokePreview(f);
      }
    };
  }, []);

  const patch = React.useCallback(
    (id: string, next: Partial<UploadedFile>) => {
      setFiles((prev) => {
        const updated = prev.map((f) => (f.id === id ? { ...f, ...next } : f));
        onChange?.(updated);
        return updated;
      });
    },
    [onChange]
  );

  const uploadOne = React.useCallback(
    async (entry: UploadedFile) => {
      patch(entry.id, { status: "uploading", progress: 0 });
      try {
        const res = await r2Service.uploadWithPresign(entry.file, group, (pct) => {
          patch(entry.id, { progress: pct });
        });
        patch(entry.id, { status: "done", progress: 100, key: res.key, url: res.url });
      } catch (err: any) {
        patch(entry.id, {
          status: "error",
          progress: 0,
          error: String(err?.message ?? err ?? "Upload failed"),
        });
      }
    },
    [group, patch]
  );

  const addFiles = React.useCallback(
    (incoming: FileList | File[] | null) => {
      if (!incoming) return;
      const picked = Array.from(incoming);
      if (picked.length === 0) return;
      const accepted: UploadedFile[] = [];
      const rejectedOversize: string[] = [];
      for (const f of picked) {
        if (maxSizeMb && f.size > maxSizeMb * 1024 * 1024) {
          rejectedOversize.push(f.name);
          continue;
        }
        accepted.push({
          id: randomId(),
          file: f,
          status: "pending",
          progress: 0,
          localPreviewUrl: makeLocalPreview(f),
        });
      }

      if (!multiple && accepted.length > 0) {
        setFiles((prev) => {
          for (const p of prev) revokePreview(p);
          return accepted;
        });
        onChange?.(accepted);
      } else if (accepted.length > 0) {
        setFiles((prev) => {
          const merged = [...prev, ...accepted];
          onChange?.(merged);
          return merged;
        });
      }

      for (const entry of accepted) uploadOne(entry);
      if (rejectedOversize.length) {
        // Surface but do not throw: caller's toast system will already notify on server-side errors.
        console.warn("Rejected oversized files:", rejectedOversize);
      }
    },
    [maxSizeMb, multiple, onChange, uploadOne]
  );

  const removeOne = (id: string) => {
    setFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      if (removed) revokePreview(removed);
      const next = prev.filter((f) => f.id !== id);
      onChange?.(next);
      return next;
    });
  };

  const retryOne = (id: string) => {
    const entry = files.find((f) => f.id === id);
    if (!entry) return;
    uploadOne({ ...entry, status: "pending", progress: 0 });
  };

  return (
    <div className={className}>
      {label ? (
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
          {label}
        </label>
      ) : null}
      <div
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragActive(false);
          addFiles(e.dataTransfer.files);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
          disabled
            ? "cursor-not-allowed opacity-60"
            : dragActive
            ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
            : "border-gray-300 bg-gray-50 hover:bg-gray-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
        }`}
      >
        <MdCloudUpload className="h-8 w-8 text-brand-500" />
        <p className="text-sm font-semibold text-gray-800 dark:text-white">
          {isArabic ? "اسحب وأفلت الملفات هنا" : "Drag & drop files here"}
        </p>
        <p className="text-xs text-gray-500">
          {hint ??
            (isArabic
              ? `اضغط لاختيار ملف${multiple ? " أو أكثر" : ""}`
              : `or click to pick ${multiple ? "one or more files" : "a file"}`)}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {files.map((entry) => {
            const lockedExt = splitFilename(entry.file.name).ext;
            const displayBase = splitFilename(entry.displayName ?? entry.file.name).base;
            return (
            <li
              key={entry.id}
              className="flex items-center gap-3 overflow-visible rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-navy-900"
            >
              <FileRowPreview entry={entry} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-0.5">
                    <FileNameInput
                      id={entry.id}
                      base={displayBase}
                      lockedExt={lockedExt}
                      disabled={disabled}
                      isArabic={isArabic}
                      onChangeName={(id, nextDisplayName) => patch(id, { displayName: nextDisplayName })}
                    />
                    {lockedExt ? (
                      <span className="shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400">
                        {lockedExt}
                      </span>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-xs text-gray-500">{formatSize(entry.file.size)}</span>
                </div>
                <div className="mt-1 flex min-w-0 items-center gap-2">
                  <div
                    className={`h-1.5 min-w-0 flex-1 overflow-hidden rounded-full ${
                      entry.status === "done"
                        ? "bg-emerald-200/90 dark:bg-emerald-900/50"
                        : entry.status === "error"
                        ? "bg-red-100 dark:bg-red-900/40"
                        : "bg-gray-200 dark:bg-white/10"
                    }`}
                  >
                    <div
                      className={`h-full rounded-full transition-[width] duration-200 ${
                        entry.status === "error"
                          ? "bg-red-500"
                          : entry.status === "done"
                          ? "bg-emerald-500"
                          : "bg-brand-500"
                      }`}
                      style={{
                        width: `${
                          entry.status === "done" || entry.status === "error"
                            ? 100
                            : entry.progress
                        }%`,
                      }}
                    />
                  </div>
                  {(entry.status === "pending" || entry.status === "uploading") && (
                    <span className="shrink-0 text-[11px] font-semibold tabular-nums text-gray-600 dark:text-gray-300">
                      {Math.min(100, Math.max(0, Math.round(entry.progress)))}%
                    </span>
                  )}
                </div>
                {entry.status === "error" && entry.error ? (
                  <p className="mt-1 text-[11px] text-red-500">{entry.error}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {entry.status === "done" && <MdCheckCircle className="h-5 w-5 text-emerald-500" />}
                {entry.status === "error" && (
                  <>
                    <MdErrorOutline className="h-5 w-5 text-red-500" />
                    <button
                      type="button"
                      onClick={() => retryOne(entry.id)}
                      className="rounded-md px-2 py-1 text-xs font-medium text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10"
                    >
                      {isArabic ? "إعادة" : "Retry"}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => removeOne(entry.id)}
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white"
                  aria-label={isArabic ? "إزالة" : "Remove"}
                >
                  <MdDelete className="h-4 w-4" />
                </button>
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
